/**
 * §P.11 · Lo que han pedido las familias.
 *
 * ## El recorrido va en un sentido, y la pantalla solo ofrece el siguiente paso
 *
 * `requested → ordered → delivered`. Volver atrás responde `409
 * request_cannot_go_back`, y con razón: marcar como «pedida» algo ya entregado no es
 * corregir un error, es **perder la fecha de entrega** — que es justo lo que el club
 * enseña cuando una familia dice que no recibió nada. Así que no hay desplegable de
 * estados: hay un botón, y dice lo que hace.
 *
 * ## La talla que llega es la que pidió la familia
 *
 * No la que sugirió Kiap. Se puede corregir aquí —un padre escribe 140 y el niño usa
 * 150— y al entregar **pasa al histórico del alumno**, que es lo que permite que el
 * septiembre siguiente se diga «el año pasado llevó una 140».
 *
 * ## Anular no es un paso del recorrido
 *
 * Es salirse de él, y por eso está aparte y en cualquier momento: una familia se
 * arrepiente, o el niño se va del club antes de que llegue el pedido.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { euros, fecha } from '@/shared/ui/formato';
import {
  ESTADOS_DE_SOLICITUD,
  siguienteEstado,
  type EstadoDeSolicitud,
  type SolicitudDeMaterial,
} from '@/club/material/tipos';

const FILTROS: readonly { id: '' | EstadoDeSolicitud; etiqueta: string }[] = [
  { id: '', etiqueta: 'Todas' },
  { id: 'requested', etiqueta: 'Sin cursar' },
  { id: 'ordered', etiqueta: 'Pedidas' },
  { id: 'delivered', etiqueta: 'Entregadas' },
];

export function Solicitudes({
  club,
  puedeEditar,
  alAbrirAlumno,
}: {
  club: string;
  puedeEditar: boolean;
  alAbrirAlumno: (alumnoId: string) => void;
}) {
  const cache = useQueryClient();
  const [filtro, setFiltro] = useState<'' | EstadoDeSolicitud>('requested');
  const [problema, setProblema] = useState<Problema | null>(null);
  const [tallas, setTallas] = useState<Record<string, string>>({});

  const solicitudes = useQuery({
    queryKey: ['solicitudes-material', club, filtro],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        ['material-requests'].get({
          query: filtro === '' ? {} : { status: filtro },
        });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as SolicitudDeMaterial[];
    },
  });

  const cambiar = useMutation({
    mutationFn: async ({
      id,
      cambios,
    }: {
      id: string;
      cambios: Record<string, unknown>;
    }) => {
      const r = await api.v1
        .clubs({ club })
        ['material-requests']({ id })
        .patch(cambios);
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setProblema(null);
      void cache.invalidateQueries({
        queryKey: ['solicitudes-material', club],
      });
      // Al entregar, la talla entra en el histórico del alumno: la lista de tallas
      // que haya abierta en otra pestaña se queda vieja si no se invalida.
      void cache.invalidateQueries({ queryKey: ['tallas', club] });
      void cache.invalidateQueries({ queryKey: ['catalogo', club] });
      void cache.invalidateQueries({ queryKey: ['campanas', club] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  if (solicitudes.isPending) {
    return <Cargando filas={4} etiqueta="Cargando las solicitudes" />;
  }

  if (solicitudes.isError || !solicitudes.data) {
    return (
      <PanelError
        problema={aProblema(solicitudes.error)}
        reintentar={() => void solicitudes.refetch()}
      />
    );
  }

  return (
    <section>
      <header className="titulo-fila">
        <h1>Solicitudes</h1>
        <nav className="pestañas" aria-label="Qué solicitudes se enseñan">
          {FILTROS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={filtro === f.id ? 'pestaña-activa' : 'btn-plano'}
              onClick={() => setFiltro(f.id)}
            >
              {f.etiqueta}
            </button>
          ))}
        </nav>
      </header>

      {problema && <PanelError problema={problema} />}

      {solicitudes.data.length === 0 ? (
        <Vacio
          titulo={
            filtro === 'requested'
              ? 'Nada pendiente de cursar'
              : 'No hay solicitudes'
          }
          explicacion={
            filtro === 'requested'
              ? 'Todo lo que han pedido las familias está ya pedido o entregado.'
              : 'Las familias piden desde su App, sobre lo que hayas publicado en el catálogo.'
          }
        />
      ) : (
        <ul className="papeles">
          {solicitudes.data.map((s) => {
            const paso = siguienteEstado(s.status);
            const nombre = `${s.first_name} ${s.last_name ?? ''}`.trim();

            return (
              <li key={s.id}>
                <div className="aviso-cabeza">
                  <strong>{s.item_name}</strong>
                  <span className="pastilla pastilla-left">
                    {ESTADOS_DE_SOLICITUD[s.status]}
                  </span>
                  {s.campaign_name && (
                    <span className="tenue">{s.campaign_name}</span>
                  )}
                </div>

                <p>
                  <button
                    type="button"
                    className="enlace-fila"
                    onClick={() => alAbrirAlumno(s.student_id)}
                  >
                    {nombre}
                  </button>
                  <span className="tenue">
                    {' '}
                    · talla {s.size ?? 'sin decir'}
                    {s.quantity > 1 && ` · ${s.quantity} unidades`} ·{' '}
                    {euros(s.total_cents)}
                  </span>
                </p>

                {s.comment && <p className="tenue">«{s.comment}»</p>}

                <p className="tenue">
                  Pedida el {fecha(s.requested_at)}
                  {s.ordered_at && ` · cursada el ${fecha(s.ordered_at)}`}
                  {s.delivered_at && ` · entregada el ${fecha(s.delivered_at)}`}
                </p>

                {puedeEditar && s.status !== 'cancelled' && (
                  <div className="acciones">
                    {paso && (
                      <button
                        type="button"
                        className="btn-primario"
                        disabled={cambiar.isPending}
                        onClick={() =>
                          cambiar.mutate({
                            id: s.id,
                            cambios: { status: paso.id },
                          })
                        }
                      >
                        {paso.etiqueta}
                      </button>
                    )}

                    {/*
                      La talla se corrige mientras no esté entregada: un padre
                      escribe 140 y el niño usa 150, y quien lo sabe es el club.
                      Después de entregar ya está en el histórico y cambiarla sería
                      reescribir lo que pasó.
                    */}
                    {s.status !== 'delivered' && (
                      <label className="fecha-en-linea">
                        Corregir la talla
                        <input
                          maxLength={20}
                          placeholder={s.size ?? '150'}
                          value={tallas[s.id] ?? ''}
                          onChange={(e) =>
                            setTallas({ ...tallas, [s.id]: e.target.value })
                          }
                          onBlur={() => {
                            const nueva = (tallas[s.id] ?? '').trim();
                            if (nueva === '' || nueva === s.size) return;
                            cambiar.mutate({
                              id: s.id,
                              cambios: { size: nueva },
                            });
                          }}
                        />
                      </label>
                    )}

                    <button
                      type="button"
                      className="btn-plano"
                      disabled={cambiar.isPending}
                      onClick={() =>
                        cambiar.mutate({
                          id: s.id,
                          cambios: { status: 'cancelled' },
                        })
                      }
                    >
                      Anular
                    </button>
                  </div>
                )}

                {s.status === 'delivered' && (
                  <p className="tenue">
                    La talla ha pasado al histórico del alumno: en septiembre se
                    podrá decir «el año pasado llevó una {s.size ?? '—'}».
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
